import { ViewBooking } from "collins-js-sdk";
import React, { useCallback, useRef } from "react";
import {
  cleanupDragClones,
  createDragClone,
  dispatchTouchDragHoverEvent,
  dispatchTouchDragHoverFromPoint,
  dispatchTouchDragModeEvent,
  dispatchTouchDropEvent,
  moveTouchDragImage,
  prepareTouchDragImage
} from "../../../../../../../utils/dragCloneUtils";

interface UseDiaryBookingCardHandlersProps {
  booking: ViewBooking & { offlineId?: string };
  touchDragCloneRef: React.MutableRefObject<HTMLElement | null>;
  isTouchDraggingRef: React.MutableRefObject<boolean>;
  onDragStateChange?: (isDragging: boolean) => void;
  onBookingChanged?: (booking: ViewBooking) => void;
}

export const useDiaryBookingCardHandlers = ({
  booking,
  touchDragCloneRef,
  isTouchDraggingRef,
  onDragStateChange
  // onBookingChanged
}: UseDiaryBookingCardHandlersProps) => {
  const activeTouchPointerIdRef = useRef<number | null>(null);
  const activeTouchButtonRef = useRef<HTMLButtonElement | null>(null);
  const lastTouchPositionRef = useRef<{ x: number; y: number } | null>(null);
  const pendingTouchFrameRef = useRef<number | null>(null);
  const queuedTouchPositionRef = useRef<{ x: number; y: number } | null>(null);

  const flushTouchFrame = useCallback(() => {
    pendingTouchFrameRef.current = null;
    const queuedPosition = queuedTouchPositionRef.current;

    if (!queuedPosition || !isTouchDraggingRef.current) {
      return;
    }

    if (touchDragCloneRef.current) {
      moveTouchDragImage(
        touchDragCloneRef.current,
        queuedPosition.x,
        queuedPosition.y
      );
    }

    dispatchTouchDragHoverFromPoint(queuedPosition.x, queuedPosition.y);
  }, [isTouchDraggingRef, touchDragCloneRef]);

  const queueTouchFrame = useCallback(() => {
    if (pendingTouchFrameRef.current !== null) {
      return;
    }

    pendingTouchFrameRef.current =
      window.requestAnimationFrame(flushTouchFrame);
  }, [flushTouchFrame]);

  const cancelTouchFrame = useCallback(() => {
    if (pendingTouchFrameRef.current !== null) {
      window.cancelAnimationFrame(pendingTouchFrameRef.current);
      pendingTouchFrameRef.current = null;
    }
    queuedTouchPositionRef.current = null;
  }, []);

  const getResolvedTouchCoordinates = useCallback(
    (clientX: number, clientY: number) => {
      if (clientX === 0 && clientY === 0 && lastTouchPositionRef.current) {
        return {
          clientX: lastTouchPositionRef.current.x,
          clientY: lastTouchPositionRef.current.y
        };
      }

      lastTouchPositionRef.current = { x: clientX, y: clientY };
      return { clientX, clientY };
    },
    []
  );

  const endTouchDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (!isTouchDraggingRef.current) {
        return;
      }

      const { clientX: resolvedClientX, clientY: resolvedClientY } =
        getResolvedTouchCoordinates(clientX, clientY);

      dispatchTouchDropEvent(booking, resolvedClientX, resolvedClientY);

      dispatchTouchDragHoverEvent(null);
      dispatchTouchDragModeEvent(false);
      onDragStateChange?.(false);
      cancelTouchFrame();
      cleanupDragClones();
      touchDragCloneRef.current = null;
      isTouchDraggingRef.current = false;

      const activeTouchButton = activeTouchButtonRef.current;
      const activePointerId = activeTouchPointerIdRef.current;
      if (
        activeTouchButton &&
        activePointerId !== null &&
        activeTouchButton.hasPointerCapture(activePointerId)
      ) {
        activeTouchButton.releasePointerCapture(activePointerId);
      }
      if (activeTouchButton) {
        activeTouchButton.draggable = true;
      }

      activeTouchButtonRef.current = null;
      activeTouchPointerIdRef.current = null;
      lastTouchPositionRef.current = null;
    },
    [
      booking,
      cancelTouchFrame,
      getResolvedTouchCoordinates,
      isTouchDraggingRef,
      onDragStateChange,
      touchDragCloneRef
    ]
  );

  const handleDragButtonPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType !== "touch") {
        return;
      }

      e.stopPropagation();
      e.preventDefault();

      const button = e.currentTarget;
      button.draggable = false;
      button.setPointerCapture(e.pointerId);

      activeTouchPointerIdRef.current = e.pointerId;
      activeTouchButtonRef.current = button;
      lastTouchPositionRef.current = {
        x: e.clientX,
        y: e.clientY
      };
      queuedTouchPositionRef.current = {
        x: e.clientX,
        y: e.clientY
      };
      onDragStateChange?.(true);

      const clone = createDragClone(booking.id ?? "");
      if (!clone) {
        button.draggable = true;
        activeTouchPointerIdRef.current = null;
        activeTouchButtonRef.current = null;
        lastTouchPositionRef.current = null;
        onDragStateChange?.(false);
        cancelTouchFrame();
        return;
      }

      const sourceCard = document.getElementById(`Booking_${booking.id ?? ""}`);
      const sourceCardRect = sourceCard?.getBoundingClientRect();
      const touchAnchor = sourceCardRect
        ? {
            offsetX: e.clientX - sourceCardRect.left,
            offsetY: e.clientY - sourceCardRect.top
          }
        : undefined;

      prepareTouchDragImage(clone, e.clientX, e.clientY, touchAnchor);
      touchDragCloneRef.current = clone;
      isTouchDraggingRef.current = true;

      dispatchTouchDragModeEvent(true);
      dispatchTouchDragHoverFromPoint(e.clientX, e.clientY);
    },
    [
      booking.id,
      cancelTouchFrame,
      touchDragCloneRef,
      isTouchDraggingRef,
      onDragStateChange
    ]
  );

  const handleDragButtonPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType !== "touch" || !isTouchDraggingRef.current) {
        return;
      }

      if (
        activeTouchPointerIdRef.current !== null &&
        e.pointerId !== activeTouchPointerIdRef.current
      ) {
        return;
      }

      e.preventDefault();
      lastTouchPositionRef.current = {
        x: e.clientX,
        y: e.clientY
      };
      queuedTouchPositionRef.current = {
        x: e.clientX,
        y: e.clientY
      };

      queueTouchFrame();
    },
    [isTouchDraggingRef, queueTouchFrame]
  );

  const handleDragButtonPointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType !== "touch") {
        return;
      }

      if (
        activeTouchPointerIdRef.current !== null &&
        e.pointerId !== activeTouchPointerIdRef.current
      ) {
        return;
      }

      e.preventDefault();
      cancelTouchFrame();
      endTouchDrag(e.clientX, e.clientY);
    },
    [cancelTouchFrame, endTouchDrag]
  );

  const handleDragButtonPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType !== "touch") {
        return;
      }

      if (
        activeTouchPointerIdRef.current !== null &&
        e.pointerId !== activeTouchPointerIdRef.current
      ) {
        return;
      }

      e.preventDefault();
      cancelTouchFrame();
      endTouchDrag(e.clientX, e.clientY);
    },
    [cancelTouchFrame, endTouchDrag]
  );

  const handleDragButtonLostPointerCapture = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType !== "touch") {
        return;
      }

      if (
        activeTouchPointerIdRef.current !== null &&
        e.pointerId !== activeTouchPointerIdRef.current
      ) {
        return;
      }

      endTouchDrag(e.clientX, e.clientY);
    },
    [endTouchDrag]
  );

  const handleDragButtonDragStart = useCallback(
    (e: React.DragEvent<HTMLButtonElement>) => {
      if (activeTouchPointerIdRef.current !== null) {
        e.preventDefault();
        return;
      }

      // Don't start native drag if we're already in touch drag mode
      if (isTouchDraggingRef.current) {
        e.preventDefault();
        return;
      }

      e.stopPropagation();
      dispatchTouchDragModeEvent(true);
      onDragStateChange?.(true);
      e.dataTransfer.setData("booking", JSON.stringify(booking));

      const clone = createDragClone(booking.id ?? "");
      if (clone) {
        const sourceCard = document.getElementById(
          `Booking_${booking.id ?? ""}`
        );
        const sourceCardRect = sourceCard?.getBoundingClientRect();
        const cloneWidth = clone.getBoundingClientRect().width || 320;
        const offsetX = sourceCardRect
          ? Math.min(Math.max(e.clientX - sourceCardRect.left, 0), cloneWidth)
          : cloneWidth / 2;
        const offsetY = sourceCardRect
          ? Math.max(e.clientY - sourceCardRect.top, 0)
          : 20;
        e.dataTransfer.setDragImage(clone, offsetX, offsetY);
      }
    },
    [booking, isTouchDraggingRef, onDragStateChange]
  );

  const handleDragButtonDragEnd = useCallback(
    (e: React.DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      dispatchTouchDragModeEvent(false);
      onDragStateChange?.(false);
      cleanupDragClones();
    },
    [onDragStateChange]
  );

  const handleDragButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  return {
    endTouchDrag,
    handleDragButtonPointerDown,
    handleDragButtonPointerMove,
    handleDragButtonPointerUp,
    handleDragButtonPointerCancel,
    handleDragButtonLostPointerCapture,
    handleDragButtonDragStart,
    handleDragButtonDragEnd,
    handleDragButtonClick
  };
};
